import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPlus, FaSpinner, FaCheck, FaTimes, FaCalendar, FaTag, FaMoneyBillWave, FaEdit } from 'react-icons/fa';
import API_URLS from '../apiUrls';

const EntryForm = ({ fetchEntries, darkMode }) => {
  const [type, setType] = useState('incoming');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Fetch user's available categories
  useEffect(() => {
    const fetchUserCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await axios.get(API_URLS.USER_CATEGORIES);
        setAvailableCategories(response.data);
        // Set the first category as default if available
        if (response.data.length > 0) {
          setCategory(response.data[0]);
        }
      } catch (error) {
        console.error('Error fetching user categories:', error);
        // Fallback to default categories if API fails
        setAvailableCategories(['School', '14starsfarm', '5starsfarm']);
        setCategory('School');
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchUserCategories();
  }, []);

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
  };

  const resetForm = () => {
    setAmount('');
    setDescription('');
    setDate('');
    setType('incoming');
    setCategory(availableCategories.length > 0 ? availableCategories[0] : '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!amount || !description || !category) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      await axios.post(
        API_URLS.ENTRIES,
        { 
          type, 
          amount, 
          description, 
          category, 
          date 
        }
      );
      
      setSubmitSuccess(true);
      resetForm();
      fetchEntries();
      
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error("Error creating entry:", error);
      alert('Error creating entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-teal-600 px-4 py-3">
        <div className="flex items-center">
          <div className="p-1.5 bg-white/20 rounded-lg">
            <FaPlus className="h-4 w-4 text-white" />
          </div>
          <div className="ml-2">
            <h3 className="text-base font-semibold text-white">Add New Transaction</h3>
            <p className="text-blue-100 text-xs">Record your financial activity</p>
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Success Message */}
        {submitSuccess && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center">
              <FaCheck className="w-4 h-4 text-green-500 mr-2" />
              <span className="text-green-700 dark:text-green-300 font-medium text-sm">
                Transaction added successfully!
              </span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Transaction Type and Amount Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Transaction Type */}
            <div>
              <label className="block text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">
                Transaction Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaMoneyBillWave className="h-4 w-4 text-gray-400" />
                </div>
                <select 
                  value={type} 
                  onChange={(e) => setType(e.target.value)} 
                  className="w-full pl-10 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200 appearance-none"
                  disabled={isSubmitting}
                >
                  <option value="incoming">💰 Incoming (Credit)</option>
                  <option value="outgoing">💸 Outgoing (Debit)</option>
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Amount */}
            <div>
              <label className="block text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">
                Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 dark:text-gray-400 font-medium text-sm">Rs.</span>
                </div>
                <input 
                  type="number" 
                  value={amount} 
                  onChange={(e) => setAmount(e.target.value)} 
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200 placeholder-gray-400 dark:placeholder-gray-500" 
                  required 
                  disabled={isSubmitting}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">
              Description <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 pt-2 pointer-events-none">
                <FaEdit className="h-4 w-4 text-gray-400" />
              </div>
              <textarea 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200 placeholder-gray-400 dark:placeholder-gray-500 resize-none"
                placeholder="Enter transaction description..."
                required
                disabled={isSubmitting}
                rows="2"
              ></textarea>
            </div>
          </div>
          
          {/* Category and Date Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Category */}
            <div>
              <label className="block text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">
                Category <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaTag className="h-4 w-4 text-gray-400" />
                </div>
                <select 
                  value={category}
                  onChange={handleCategoryChange}
                  className="w-full pl-10 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200 appearance-none"
                  disabled={isSubmitting || categoriesLoading}
                >
                  {categoriesLoading ? (
                    <option>Loading categories...</option>
                  ) : availableCategories.length > 0 ? (
                    availableCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))
                  ) : (
                    <option value="">No categories available</option>
                  )}
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Date */}
            <div>
              <label className="block text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">
                Date (Optional)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaCalendar className="h-4 w-4 text-gray-400" />
                </div>
                <input 
                  type="datetime-local" 
                  value={date} 
                  onChange={(e) => setDate(e.target.value)} 
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200" 
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
          
          {/* Submit Button */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              className={`flex-1 bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 text-sm ${
                isSubmitting ? 'opacity-50 cursor-not-allowed transform-none' : 'shadow hover:shadow-md'
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <FaSpinner className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Adding Transaction...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <FaPlus className="mr-2 h-4 w-4" />
                  Add Transaction
                </div>
              )}
            </button>
            
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 text-sm"
              disabled={isSubmitting}
            >
              <div className="flex items-center">
                <FaTimes className="mr-2 h-4 w-4" />
                Clear
              </div>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EntryForm;
